import React, { useState, useEffect } from "react";

// Helper hook for responsive checks
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [matches, query]);
  return matches;
}

function SearchTutorial({ onClose, activeTab, context }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ 
    top: "50%", 
    left: "50%", 
    transform: "translate(-50%, -50%)", 
    placement: "center", 
    position: "fixed" 
  });

  const { selectedTags, qInput, searching, rows, filtersOpen } = context || {};
  
  // Detect mobile state matching CSS breakpoint
  const isMobile = useMediaQuery("(max-width: 900px)");

  const handleClose = (completed = false) => {
    if (completed) {
      localStorage.setItem("searchTutorialCompleted", "true");
    }
    onClose();
  };

  // --- DYNAMIC STEP 3 CONFIGURATION ---
  const step3Config = (isMobile && !filtersOpen) ? {
    title: "Open Search Options",
    description: "Tap the 'Search terms' button to open the filter menu and set your criteria.",
    selectors: [".psl-filters-toggle"],
    position: "bottom",
    validate: () => filtersOpen,
    hint: "Click the button to open the menu."
  } : {
    title: "Define Your Criteria",
    description: "You must define what you are looking for. Type a custom genre in the search bar OR select a keyword tag below.",
    selectors: [".psl-search", ".psl-group:not(.psl-last)"], 
    position: "bottom",
    validate: () => (qInput && qInput.trim().length > 0) || (selectedTags && selectedTags.size > 0),
    hint: "Please type a keyword or select a tag to continue.",
  };

  // --- STEPS CONFIGURATION ---
  const steps = [
    {
      title: "Welcome to Playlist Search",
      description: "This tool helps you find Spotify playlists with curator contact information. Let's get you set up to find your first playlist.",
      position: "center",
      isIntro: true,
    },
    {
      title: "Search Modes",
      description: "Choose 'Live Search' for fresh results or 'Database' for instant cached results.",
      selectors: [".psl-tabs"],
      position: "bottom",
    },
    step3Config,
    {
      title: "Contact Requirements",
      description: "Select 'Email', 'IG', or both. This filters results to ensure you only see playlists with the contact info you need.",
      selectors: [".psl-group.psl-last"],
      position: "bottom",
    },
    {
      title: "Launch Search",
      description: "Click the Search button to begin crawling for playlists. The menu will close automatically.",
      selectors: [".psl-actions .psl-btn.primary"],
      position: "top",
      hideNext: true,
      advanceOnAction: "search",
    },
    {
      title: "Reveal Individual Contacts",
      description: "Results are here! You can click 'Reveal' on any specific row to unlock its contact info immediately. (This uses 1 credit).",
      selectors: [
        ".psl-table th:last-child", 
        ".psl-table tbody tr:nth-child(-n+3) td:last-child"
      ], 
      position: "left",
      requiresResults: true,
    },
    {
      title: "Bulk Reveal",
      description: "To work faster, check the boxes on the left to select multiple playlists, then use the 'Reveal Selected' button above.",
      selectors: [
        // 1. The Master Checkbox input (Header)
        "input[aria-label='Select all on this page']",
        // 2. The first 5 row checkboxes only (keeps the highlight short and vertical)
        ".psl-table tbody tr:nth-child(-n+5) input.psl-chk"
      ],
      position: "right",
    },
    {
      title: "View Revealed Contacts",
      description: "Click the 'Revealed' tab to see all the contacts you have unlocked and prepare them for export.",
      selectors: [".psl-tabwrap:nth-child(3)"], 
      position: "bottom",
      hideNext: true,
      advanceOnAction: "tabSwitch", 
    },
    {
      title: "Export or Campaign",
      description: "Now you are in your library. Use 'Add to Campaign' to start an email sequence, or 'Export' to download a CSV file.",
      selectors: [".psl-actions-row"],
      position: "bottom",
    },
  ];

  // --- AUTO ADVANCE LOGIC ---
  useEffect(() => {
    if (currentStep >= steps.length) return;

    const step = steps[currentStep];

    if (step.advanceOnAction === "search" && searching) {
      setTimeout(() => {
        setCurrentStep(prev => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 500);
    }
    if (step.advanceOnAction === "tabSwitch" && activeTab === "revealed") {
      setTimeout(() => {
        setCurrentStep(prev => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 400); 
    }
  }, [currentStep, searching, activeTab, steps.length]);

  // --- POSITIONING & HIGHLIGHT LOGIC ---
  useEffect(() => {
    if (currentStep >= steps.length) return;

    const updateLayout = () => {
      const step = steps[currentStep];
      if (!step) return;

      if (step.selectors && step.selectors.length > 0) {
        let minTop = Infinity, minLeft = Infinity, maxBottom = -Infinity, maxRight = -Infinity;
        let found = false;

        step.selectors.forEach(sel => {
          const elements = document.querySelectorAll(sel);
          elements.forEach(el => {
            const rect = el.getBoundingClientRect();
            // Ensure visible (width/height > 0)
            if (rect.width > 0 && rect.height > 0) {
              found = true;
              minTop = Math.min(minTop, rect.top);
              minLeft = Math.min(minLeft, rect.left);
              maxBottom = Math.max(maxBottom, rect.bottom);
              maxRight = Math.max(maxRight, rect.right);
            }
          });
        });

        if (found) {
          const scrollY = window.scrollY;
          const scrollX = window.scrollX;
          const finalRect = {
            top: minTop + scrollY,
            left: minLeft + scrollX,
            width: maxRight - minLeft,
            height: maxBottom - minTop
          };
          setHighlightRect(finalRect);
          calculateTooltipPos(finalRect, step.position);
        } else {
          setHighlightRect(null); 
        }
      } else {
        // Intro Step
        setHighlightRect(null);
        setTooltipPos({ 
          top: "50%", 
          left: "50%", 
          transform: "translate(-50%, -50%)", 
          placement: "center", 
          position: "fixed" 
        });
      }
    };

    const calculateTooltipPos = (rect, preferredPosition) => {
      const gap = 16;
      const tooltipW = 320;
      let top, left;
      
      if (preferredPosition === "top") {
        top = rect.top - gap; 
        left = rect.left + (rect.width / 2) - (tooltipW / 2);
      } else if (preferredPosition === "bottom") {
        top = rect.top + rect.height + gap;
        left = rect.left + (rect.width / 2) - (tooltipW / 2);
      } else if (preferredPosition === "left") {
        top = rect.top;
        left = rect.left - gap - tooltipW;
      } else if (preferredPosition === "right") {
        top = rect.top;
        left = rect.left + rect.width + gap;
      }

      if (left < 10) left = 10;
      if (left + tooltipW > window.innerWidth) left = window.innerWidth - tooltipW - 10;
      
      let transform = "none";
      if (preferredPosition === "top") transform = "translateY(-100%)";
      
      setTooltipPos({ top, left, transform, placement: preferredPosition, position: "absolute" });
    };

    // Immediate update + Listeners
    updateLayout(); 
    const t = setTimeout(updateLayout, 300); 

    window.addEventListener("resize", updateLayout);
    window.addEventListener("scroll", updateLayout);
    const observer = new MutationObserver(updateLayout);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener("scroll", updateLayout);
      observer.disconnect();
    };
  }, [currentStep, rows, filtersOpen, isMobile, steps.length]);

  // --- RENDER ---
  const step = steps[currentStep];

  if (!step) return null;

  const canProceed = step.validate ? step.validate() : true;
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(s => s + 1);
    else handleClose(true);
  };

  const handleSkip = () => handleClose(true);
  const handlePrev = () => currentStep > 0 && setCurrentStep(s => s - 1);

  return (
    <div style={{position:'absolute', top:0, left:0, width:'100%', height:'0px', zIndex: 9999}}>
      {step.isIntro && <div className="psl-tutorial-backdrop" />}

      {highlightRect && (
        <div
          className="psl-tutorial-highlight active"
          style={{
            top: highlightRect.top - 6,
            left: highlightRect.left - 6,
            width: highlightRect.width + 12,
            height: highlightRect.height + 12,
          }}
        />
      )}

      <div
        className="psl-tutorial-tooltip"
        style={{
          position: tooltipPos.position || "absolute", 
          top: tooltipPos.top,
          left: tooltipPos.left,
          transform: tooltipPos.transform,
        }}
        data-placement={tooltipPos.placement}
      >
        <div className="psl-tutorial-content">
          <div className="psl-tutorial-header">
            <div className="psl-tutorial-stepnum">{currentStep + 1}</div>
            <h3>{step.title}</h3>
          </div>
          
          <p>{step.description}</p>

          {!canProceed && step.hint && (
            <div className="psl-tutorial-hint warning">{step.hint}</div>
          )}

          <div className="psl-tutorial-controls">
            <button className="psl-btn" onClick={handleSkip} style={{marginRight:'auto', color:'#6b7280', border:'none', paddingLeft:0}}>
              Skip
            </button>
            <button className="psl-btn" onClick={handlePrev} disabled={currentStep === 0}>
              Prev
            </button>
            {!step.hideNext && (
              <button className="psl-btn primary" onClick={handleNext} disabled={!canProceed}>
                {currentStep === steps.length - 1 ? "Finish" : "Next"}
              </button>
            )}
          </div>
        </div>
        {!step.isIntro && <div className="psl-tutorial-arrow" />}
      </div>
    </div>
  );
}

export default SearchTutorial;